import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Search, Package, MapPin, Building2, Star, Filter,
  Globe, ChevronRight, ChevronLeft, Send, CheckCircle, Loader2,
  Phone, Mail, Shield, Sparkles, Target, TrendingUp,
  Menu, X, Upload, Image, Lock, FileText, Ship, Eye, BadgeCheck, Heart, HeartOff
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import { useLanguage } from '@/context/LanguageContext';
import { VerificationStatus, FullyVerifiedBadge, PhoneVerifiedBadge, GSTVerifiedBadge, IECVerifiedBadge, PartialVerificationBadge } from '@/components/VerificationBadges';
import { FoundingExporterBadge, UserPlanBadge } from '@/components/FoundingBadge';
import SEO, { SEO_CONFIGS, getCategorySEO } from '@/components/SEO';
import { getCountryFlag } from '@/utils/countryFlags';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Marketplace() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [currencyRates, setCurrencyRates] = useState(null);
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  
  // AI Smart Match state
  const [isSmartMatching, setIsSmartMatching] = useState(false);
  const [smartMatchResults, setSmartMatchResults] = useState(null);
  const [showSmartResults, setShowSmartResults] = useState(false);
  
  // Inquiry form state
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    quantity: '',
    message: '',
    images: []
  });
  const [isSending, setIsSending] = useState(false);
  const [inquirySent, setInquirySent] = useState(false);
  const [inquiryImagePreviews, setInquiryImagePreviews] = useState([]);
  const inquiryFileInputRef = useRef(null);
  
  // Translation state
  const [translatedDescriptions, setTranslatedDescriptions] = useState({});
  const [isTranslating, setIsTranslating] = useState({});
  
  // Watchlist state
  const [watchlist, setWatchlist] = useState([]);
  const [addingToWatchlist, setAddingToWatchlist] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const PRODUCTS_PER_PAGE = 24;

  useEffect(() => {
    // Check for logged-in user
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    // Load preferred currency from localStorage
    const savedCurrency = localStorage.getItem('preferredCurrency') || 'USD';
    setPreferredCurrency(savedCurrency);
    
    fetchProducts();
    fetchCurrencyRates();
  }, []);

  // Fetch watchlist when user is a buyer
  useEffect(() => {
    if (user?.role === 'buyer') {
      fetchWatchlist();
    }
  }, [user]);

  const fetchWatchlist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/buyer/watchlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(response.data || []);
    } catch (error) {
      console.error('Failed to fetch watchlist');
    }
  };

  const isInWatchlist = (productId) => {
    return watchlist.some(item => item.product_id === productId);
  };

  const handleAddToWatchlist = async (product, e) => {
    e?.stopPropagation();
    
    if (!user) {
      setShowAuthPrompt(true);
      return;
    }
    
    if (user.role !== 'buyer') {
      toast.error('Only buyers can add products to watchlist');
      return;
    }
    
    const productId = product.id;
    setAddingToWatchlist(prev => ({ ...prev, [productId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      
      if (isInWatchlist(productId)) {
        // Remove from watchlist
        await axios.delete(`${API}/buyer/watchlist/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWatchlist(prev => prev.filter(item => item.product_id !== productId));
        toast.success('Removed from watchlist');
      } else {
        // Add to watchlist
        await axios.post(`${API}/buyer/watchlist`, {
          product_id: productId,
          product_name: product.title,
          hs_code: product.hs_code || '',
          target_price: product.price
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWatchlist(prev => [...prev, { product_id: productId }]);
        toast.success('Added to watchlist');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update watchlist');
    } finally {
      setAddingToWatchlist(prev => ({ ...prev, [productId]: false }));
    }
  };

  const fetchCurrencyRates = async () => {
    try {
      const response = await axios.get(`${API}/currency/rates?base=INR`);
      setCurrencyRates(response.data.rates);
    } catch (error) {
      console.error('Failed to fetch currency rates');
    }
  };

  // Currency symbol mapping
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'AED': 'د.إ',
      'SAR': '﷼',
      'SGD': 'S$',
      'JPY': '¥',
      'CNY': '¥',
      'AUD': 'A$'
    };
    return symbols[currency] || currency + ' ';
  };

  const convertPrice = (price, targetCurrency = preferredCurrency, sourceCurrency = 'INR') => {
    if (!currencyRates || !price) return null;
    
    // First convert to INR (our base), then to target
    let priceInINR = price;
    if (sourceCurrency !== 'INR' && currencyRates[sourceCurrency]) {
      // If source is not INR, convert to INR first
      priceInINR = price / currencyRates[sourceCurrency];
    }
    
    // Now convert INR to target
    const rate = currencyRates[targetCurrency];
    if (!rate) return null;
    return (priceInINR * rate).toFixed(2);
  };

  const handleCurrencyChange = (currency) => {
    setPreferredCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
  };

  // Batch translate descriptions when language changes (lazy-loaded)
  useEffect(() => {
    const batchTranslateDescriptions = async () => {
      if (t === undefined || products.length === 0) return;
      
      const currentLang = localStorage.getItem('preferredLanguage') || 'en';
      if (currentLang === 'en') {
        // Reset to original descriptions
        setTranslatedDescriptions({});
        return;
      }

      // Collect products that need translation (not already translated)
      const productsToTranslate = products.filter(
        p => p.description && !translatedDescriptions[`${p.id}_${currentLang}`]
      );
      
      if (productsToTranslate.length === 0) return;

      // Batch translate (up to 10 at a time to avoid large payloads)
      const BATCH_SIZE = 10;
      for (let i = 0; i < productsToTranslate.length; i += BATCH_SIZE) {
        const batch = productsToTranslate.slice(i, i + BATCH_SIZE);
        
        // Mark batch as translating
        const translatingState = {};
        batch.forEach(p => { translatingState[p.id] = true; });
        setIsTranslating(prev => ({ ...prev, ...translatingState }));
        
        try {
          // Batch request - combine descriptions with separator
          const textsToTranslate = batch.map(p => p.description).join('\n---SPLIT---\n');
          
          const response = await axios.post(`${API}/ai/translate`, {
            text: textsToTranslate,
            target_language: currentLang,
            context: 'product_descriptions_batch'
          });
          
          // Split response back into individual translations
          const translatedTexts = (response.data.translated_text || '').split('\n---SPLIT---\n');
          
          const newTranslations = {};
          batch.forEach((product, idx) => {
            newTranslations[`${product.id}_${currentLang}`] = translatedTexts[idx]?.trim() || product.description;
          });
          
          setTranslatedDescriptions(prev => ({ ...prev, ...newTranslations }));
        } catch (error) {
          console.error('Batch translation failed:', error);
          // Fallback: keep original descriptions
        } finally {
          // Clear translating state for batch
          const clearedState = {};
          batch.forEach(p => { clearedState[p.id] = false; });
          setIsTranslating(prev => ({ ...prev, ...clearedState }));
        }
      }
    };

    // Debounce to avoid rapid re-translations
    const timeoutId = setTimeout(batchTranslateDescriptions, 300);
    return () => clearTimeout(timeoutId);
  }, [t, products]);

  // Helper function to get translated description
  const getTranslatedDescription = (product) => {
    const currentLang = localStorage.getItem('preferredLanguage') || 'en';
    if (currentLang === 'en') return product.description;
    
    const translatedKey = `${product.id}_${currentLang}`;
    return translatedDescriptions[translatedKey] || product.description;
  };

  // Compute filteredProducts inline on every render (no state, no useEffect race condition)
  const filteredProducts = useMemo(() => {
    if (showSmartResults) return products; // Smart results handled separately
    
    let filtered = [...products];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query) ||
        p.seller_name?.toLowerCase().includes(query)
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    return filtered;
  }, [products, searchQuery, selectedCategory, showSmartResults]);

  const fetchProducts = async (page = 1) => {
    try {
      setIsLoading(true);
      setLoadError(false);
      // Fetch products WITHOUT full media (only a 1-image thumbnail) for faster grid load
      // Full images load on ProductDetail page
      const response = await axios.get(`${API}/products?include_media=false&page=${page}&limit=${PRODUCTS_PER_PAGE}`, { timeout: 15000 });
      const data = response.data;
      const productsList = data.products || data || [];
      const pagination = data.pagination || {};
      setProducts(productsList);
      setCurrentPage(pagination.page || data.page || 1);
      setTotalPages(pagination.total_pages || data.total_pages || 1);
      setTotalProducts(pagination.total || data.total || productsList.length);
    } catch (error) {
      console.error('Failed to fetch products', error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchProducts(newPage);
      // Scroll to top of products
      window.scrollTo({ top: 300, behavior: 'smooth' });
    }
  };

  const handleSmartSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter what you\'re looking for');
      return;
    }
    
    setIsSmartMatching(true);
    setShowSmartResults(false);
    
    try {
      const response = await axios.post(`${API}/ai/smart-match`, {
        query: searchQuery
      });
      
      setSmartMatchResults(response.data);
      setShowSmartResults(true);
      
      if (response.data.matches?.length === 0) {
        toast.info('No exact matches found. Showing all products.');
        setShowSmartResults(false);
      }
    } catch (error) {
      toast.error('Smart matching failed. Showing regular results.');
      setShowSmartResults(false);
    } finally {
      setIsSmartMatching(false);
    }
  };

  const clearSmartResults = () => {
    setShowSmartResults(false);
    setSmartMatchResults(null);
    setSearchQuery('');
  };

  const getCategories = () => {
    const cats = [...new Set(products.map(p => p.category).filter(Boolean))];
    return ['all', ...cats];
  };

  const handleInquire = (product) => {
    setSelectedProduct(product);
    
    if (user) {
      setInquiryForm({
        name: user.full_name || '',
        company: user.company_name || '',
        email: user.email || '',
        phone: user.phone || '',
        quantity: '',
        message: ''
      });
      setShowInquiryDialog(true);
    } else {
      setShowAuthPrompt(true);
    }
  };

  const handleSendInquiry = async () => {
    if (!inquiryForm.message.trim()) {
      toast.error('Please enter your inquiry message');
      return;
    }
    
    setIsSending(true);
    
    try {
      if (user) {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/inquiries`, {
          product_id: selectedProduct.id,
          message: inquiryForm.message,
          quantity: parseInt(inquiryForm.quantity) || 1,
          images: inquiryForm.images
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/leads/widget`, {
          seller_id: selectedProduct.seller_id,
          name: inquiryForm.name,
          company: inquiryForm.company,
          email: inquiryForm.email,
          phone: inquiryForm.phone,
          product_interest: selectedProduct.title,
          message: inquiryForm.message,
          quantity: parseInt(inquiryForm.quantity) || 1,
          images: inquiryForm.images
        });
      }
      
      setInquirySent(true);
      toast.success('Inquiry sent successfully!');
      
      setTimeout(() => {
        setShowInquiryDialog(false);
        setInquirySent(false);
        setInquiryForm({
          name: '', company: '', email: '', phone: '', quantity: '', message: '', images: []
        });
        setInquiryImagePreviews([]);
      }, 3000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setIsSending(false);
    }
  };

  const handleInquiryImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (inquiryForm.images.length + files.length > 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB.`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // For guest users, store base64 directly (will be handled by backend)
      // For logged in users, upload to server
      if (user) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${API}/inquiry-images/upload`,
            { image: base64 },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setInquiryForm(prev => ({
            ...prev,
            images: [...prev.images, response.data.url]
          }));
        } catch (error) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }
      } else {
        // For guests, just store base64
        setInquiryForm(prev => ({
          ...prev,
          images: [...prev.images, base64]
        }));
      }
      setInquiryImagePreviews(prev => [...prev, base64]);
    }

    if (inquiryFileInputRef.current) {
      inquiryFileInputRef.current.value = '';
    }
  };

  const removeInquiryImage = (index) => {
    setInquiryForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setInquiryImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleGuestInquiry = () => {
    setShowAuthPrompt(false);
    setInquiryForm({
      name: '', company: '', email: '', phone: '', quantity: '', message: '', images: []
    });
    setInquiryImagePreviews([]);
    setShowInquiryDialog(true);
  };

  // Product Card Component for reuse
  const ProductCard = ({ product, matchInfo }) => (
    <Card 
      className="overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col h-full cursor-pointer"
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/product/${product.id}`)}
    >
      {/* Product Image */}
      <div 
        className="h-40 sm:h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center relative overflow-hidden"
        onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
      >
        {product.thumbnail || (product.images && product.images.length > 0) ? (
          <img 
            src={product.thumbnail || product.images[0]} 
            alt={product.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <Package className="h-12 w-12 sm:h-16 sm:w-16 text-slate-400" />
        )}
        
        {/* Match Score Badge */}
        {matchInfo && (
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <Badge className={`${matchInfo.recommended ? 'bg-green-500' : 'bg-amber-500'} text-white`}>
              <Target className="h-3 w-3 mr-1" />
              {matchInfo.match_score}% Match
            </Badge>
            {matchInfo.recommended && (
              <Badge className="bg-blue-600 text-white text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                Recommended
              </Badge>
            )}
          </div>
        )}
        
        {/* Verified Badge */}
        {product.seller_verified && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-blue-500 text-white">
              <Shield className="h-3 w-3 mr-1" />
              Verified
            </Badge>
          </div>
        )}
        
        {/* New Listing Badge - Shows for products created within 7 days */}
        {(() => {
          const createdAt = new Date(product.created_at);
          const now = new Date();
          const daysDiff = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
          return daysDiff <= 7;
        })() && (
          <div className={`absolute ${product.seller_verified ? 'top-10' : 'top-2'} right-2`}>
            <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white animate-pulse">
              <Sparkles className="h-3 w-3 mr-1" />
              New
            </Badge>
          </div>
        )}
      </div>
      
      <CardHeader className="pb-2 flex-none">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle 
              className="font-heading text-base sm:text-lg line-clamp-1 hover:text-primary cursor-pointer"
              onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
            >
              {product.title}
            </CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1 text-xs sm:text-sm">
              <span 
                className="flex items-center gap-1 hover:text-primary cursor-pointer"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (user && product.seller_id) {
                    navigate(`/seller/${product.seller_id}`);
                  } else if (!user) {
                    setShowAuthPrompt(true);
                  }
                }}
              >
                {product.company_logo ? (
                  <img 
                    src={product.company_logo} 
                    alt={product.seller_name}
                    className="h-5 w-5 rounded-full object-cover flex-shrink-0 border border-gray-200"
                  />
                ) : (
                  <Building2 className="h-3 w-3 flex-shrink-0" />
                )}
                <span className="truncate hover:underline">{product.seller_name}</span>
                {/* Verified badge for fully verified sellers */}
                <FullyVerifiedBadge 
                  phoneVerified={product.phone_verified}
                  gstinVerified={product.gstin_verified}
                  iecStatus={product.iec_status}
                  size="small"
                  variant="compact"
                />
              </span>
            </CardDescription>
            {/* Location with Country Flag */}
            {(product.district || product.origin_country || product.seller_country) && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <span className="text-base">{getCountryFlag(product.seller_country || product.origin_country)}</span>
                <MapPin className="h-3 w-3" />
                {product.district ? `${product.district}, ${product.seller_country || product.origin_country || 'India'}` : (product.seller_country || product.origin_country)}
              </p>
            )}
          </div>
        </div>
        
        {/* Verification Badges - New unique design */}
        <div className="mt-2">
          {user ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {/* Founding Exporter Badge - Top priority */}
              {product.founding_member && (
                <FoundingExporterBadge size="small" showTooltip={true} />
              )}
              {/* Show individual unique badges */}
              <PhoneVerifiedBadge verified={product.phone_verified} size="small" />
              <GSTVerifiedBadge verified={product.gstin_verified} size="small" />
              <IECVerifiedBadge status={product.iec_status} size="small" />
              {/* Show partial badge if not all verified */}
              <PartialVerificationBadge 
                phoneVerified={product.phone_verified}
                gstinVerified={product.gstin_verified}
                iecStatus={product.iec_status}
                size="small"
              />
            </div>
          ) : (
            /* Blurred badges for guests */
            <div className="flex flex-wrap gap-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed">
                <Lock className="h-2.5 w-2.5" />
                <span className="blur-[2px]">Phone</span>
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed">
                <Lock className="h-2.5 w-2.5" />
                <span className="blur-[2px]">GSTIN</span>
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed">
                <Lock className="h-2.5 w-2.5" />
                <span className="blur-[2px]">IEC</span>
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col">
        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-3 flex-1">
          {isTranslating[product.id] ? (
            <span className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Translating...
            </span>
          ) : (
            getTranslatedDescription(product)
          )}
        </p>
        
        {/* Match Reasons - Show 3 reasons */}
        {matchInfo?.reasons && matchInfo.reasons.length > 0 && (
          <div className="mb-3 p-2 bg-green-50 rounded-lg border border-green-100">
            <p className="text-xs font-medium text-green-800 mb-1 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Why this seller matches:
            </p>
            <ul className="text-xs text-green-700 space-y-0.5">
              {matchInfo.reasons.slice(0, 3).map((reason, idx) => (
                <li key={idx} className="flex items-start gap-1">
                  <span className="font-semibold text-green-800">{idx + 1}.</span>
                  <span>{reason.replace(/^Reason \d+:\s*/i, '').replace(/^\d+\.\s*/, '')}</span>
                </li>
              ))}
            </ul>
            {matchInfo.moq_fit && (
              <p className="text-xs mt-1 pt-1 border-t border-green-200">
                <span className={`font-medium ${matchInfo.moq_fit === 'Meets MOQ' ? 'text-green-700' : 'text-amber-600'}`}>
                  MOQ: {matchInfo.moq_fit}
                </span>
                {matchInfo.estimated_total && (
                  <span className="text-green-600 ml-2">• Est. {matchInfo.estimated_total}</span>
                )}
              </p>
            )}
          </div>
        )}
        
        {/* Fallback for old match_reason format */}
        {matchInfo && !matchInfo.reasons && matchInfo.match_reason && (
          <div className="mb-3 p-2 bg-green-50 rounded-lg">
            <p className="text-xs text-green-700">
              <Sparkles className="h-3 w-3 inline mr-1" />
              {matchInfo.match_reason}
            </p>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xl sm:text-2xl font-bold text-primary">
              {getCurrencySymbol(product.currency || 'INR')}{product.price?.toLocaleString() || product.price}
            </p>
            {/* Currency conversion for logged-in buyers */}
            {user && currencyRates && preferredCurrency !== (product.currency || 'INR') && (
              <p className="text-xs text-muted-foreground">
                ≈ {preferredCurrency} {convertPrice(product.price, preferredCurrency, product.currency)}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              per {product.unit || 'unit'}
            </p>
          </div>
          {product.origin_country && (
            <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{product.origin_country}</span>
              <span className="sm:hidden">{product.origin_country?.slice(0, 3)}</span>
            </div>
          )}
        </div>
        
        {/* Specifications */}
        {product.specifications && Object.keys(product.specifications).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {Object.entries(product.specifications).slice(0, 2).map(([key, value]) => (
              <Badge key={key} variant="outline" className="text-xs">
                {key}: {value}
              </Badge>
            ))}
          </div>
        )}
        
        <div className="flex gap-2 mt-auto">
          <Button 
            variant="outline"
            className="flex-1"
            onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
            data-testid={`view-details-btn-${product.id}`}
          >
            View Details
          </Button>
          {user?.role === 'buyer' && (
            <Button 
              variant={isInWatchlist(product.id) ? "secondary" : "outline"}
              size="sm"
              onClick={(e) => handleAddToWatchlist(product, e)}
              disabled={addingToWatchlist[product.id]}
              className="px-3"
              data-testid={`watchlist-btn-${product.id}`}
            >
              {addingToWatchlist[product.id] ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isInWatchlist(product.id) ? (
                <Heart className="h-4 w-4 fill-red-500 text-red-500" />
              ) : (
                <Heart className="h-4 w-4" />
              )}
            </Button>
          )}
          <Button 
            className="flex-1"
            onClick={(e) => { e.stopPropagation(); handleInquire(product); }}
            data-testid={`inquire-btn-${product.id}`}
          >
            <Send className="h-4 w-4 mr-2" />
            {user ? "Inquire" : "Send Inquiry"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Dynamic SEO based on selected category
  const currentSEO = selectedCategory && selectedCategory !== 'All' 
    ? getCategorySEO(selectedCategory)
    : SEO_CONFIGS.marketplace;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...currentSEO} />
      {/* Header - Mobile Responsive */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-10 w-auto" />
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSelector variant="ghost" />
              {user ? (
                <>
                  <span className="text-sm text-muted-foreground">Welcome, {user.full_name}</span>
                  <Button onClick={() => navigate('/dashboard')}>{t.nav.dashboard}</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/auth')}>{t.nav.login}</Button>
                  <Button onClick={() => navigate('/auth')}>{t.nav.register}</Button>
                </>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t mt-3 space-y-2">
              <div className="px-2 pb-2">
                <LanguageSelector variant="outline" />
              </div>
              {user ? (
                <>
                  <p className="text-sm text-muted-foreground px-2">Welcome, {user.full_name}</p>
                  <Button className="w-full" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>
                    {t.nav.dashboard}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full" onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}>
                    {t.nav.login}
                  </Button>
                  <Button className="w-full" onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}>
                    {t.nav.register}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - Mobile Responsive */}
      <section className="bg-gradient-to-r from-primary to-blue-700 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-4">
            Find Verified Suppliers
          </h1>
          <p className="text-base sm:text-lg text-blue-100 mb-6 sm:mb-8">
            AI-powered matching with trusted exporters
          </p>
          
          {/* Smart Search Bar */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-2xl">
            <div className="flex-1 relative">
              <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-amber-400" />
              <Input
                placeholder="Try: '4 tons of basmati rice' or 'cotton fabric 5000 meters'"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSmartSearch()}
                className="pl-10 h-12 text-slate-900 text-sm sm:text-base bg-white"
                data-testid="marketplace-search"
              />
            </div>
            <Button 
              onClick={handleSmartSearch}
              disabled={isSmartMatching}
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 h-12 px-6 whitespace-nowrap"
            >
              {isSmartMatching ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  <span className="hidden sm:inline">AI Match</span>
                  <span className="sm:hidden">Match</span>
                </>
              )}
            </Button>
          </div>
          
          <p className="text-xs sm:text-sm text-blue-200 mt-3">
            <Sparkles className="h-3 w-3 inline mr-1" />
            AI understands quantity, quality, and finds the best sellers for you
          </p>
        </div>
      </section>

      {/* Smart Match Results Banner */}
      {showSmartResults && smartMatchResults && (
        <section className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-amber-900">AI Smart Match Results</span>
                </div>
                {smartMatchResults.parsed_query && (
                  <p className="text-sm text-amber-700 mt-1">
                    Looking for: <span className="font-medium">{smartMatchResults.parsed_query.product_type}</span>
                    {smartMatchResults.parsed_query.quantity && (
                      <span className="font-medium"> ({smartMatchResults.parsed_query.quantity.toLocaleString()} {smartMatchResults.parsed_query.unit || 'units'})</span>
                    )}
                    {smartMatchResults.parsed_query.delivery_location && (
                      <span> to {smartMatchResults.parsed_query.delivery_location}</span>
                    )}
                  </p>
                )}
                {smartMatchResults.summary && (
                  <p className="text-sm font-medium text-amber-800 mt-1">
                    {smartMatchResults.summary}
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={clearSmartResults} className="self-start sm:self-center">
                Clear & Show All
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Category Tabs - Scrollable on Mobile */}
      {!showSmartResults && (
        <section className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {getCategories().map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className="whitespace-nowrap flex-shrink-0"
                >
                  {cat === 'all' ? 'All' : cat}
                </Button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products Grid - Responsive */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {loadError ? (
          <div className="text-center py-20">
            <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-medium text-slate-600 mb-2">Could not load products</h3>
            <p className="text-muted-foreground mb-4">There was a problem reaching the marketplace. Please try again.</p>
            <Button onClick={() => fetchProducts(currentPage)}>Retry</Button>
          </div>
        ) : isLoading ? (
          /* Skeleton Grid - Lightweight placeholder while loading */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="h-5 w-40 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border overflow-hidden">
                  <div className="aspect-[4/3] bg-slate-200 animate-pulse"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-5 bg-slate-200 rounded animate-pulse w-3/4"></div>
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-full"></div>
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3"></div>
                    <div className="flex justify-between items-center pt-2">
                      <div className="h-6 bg-slate-200 rounded animate-pulse w-20"></div>
                      <div className="h-4 bg-slate-100 rounded animate-pulse w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : showSmartResults && smartMatchResults?.matches?.length > 0 ? (
          /* Smart Match Results */
          <>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-muted-foreground">
                Found {smartMatchResults.matches.length} matching products
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {smartMatchResults.matches.map((match) => (
                <ProductCard 
                  key={match.product.id} 
                  product={match.product} 
                  matchInfo={match}
                />
              ))}
            </div>
          </>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-medium text-slate-600 mb-2">No products found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        ) : (
          /* Regular Product Grid */
          <>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-muted-foreground">
                Showing {filteredProducts.length} of {totalProducts} products
                {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && !showSmartResults && (
              <div className="flex items-center justify-center gap-2 mt-8 pb-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="hidden sm:flex"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="hidden sm:flex"
                >
                  Last
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Auth Prompt Dialog */}
      <Dialog open={showAuthPrompt} onOpenChange={setShowAuthPrompt}>
        <DialogContent className="mx-4 sm:mx-auto max-w-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Inquiry</DialogTitle>
            <DialogDescription>
              Create an account to track your inquiries
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <Button className="w-full" onClick={() => navigate('/auth')}>
              Login / Register
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">Or</span>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={handleGuestInquiry}>
              Continue as Guest
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inquiry Dialog - Mobile Responsive */}
      <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
        <DialogContent className="mx-4 sm:mx-auto max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto">
          {inquirySent ? (
            <div className="py-8 text-center">
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">Inquiry Sent!</h3>
              <p className="text-muted-foreground">
                The seller will respond shortly.
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">Send Inquiry</DialogTitle>
                <DialogDescription className="text-sm">
                  Contact {selectedProduct?.seller_name} about {selectedProduct?.title}
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                {!user && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm">Your Name *</Label>
                        <Input
                          value={inquiryForm.name}
                          onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                          placeholder="Full name"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Company</Label>
                        <Input
                          value={inquiryForm.company}
                          onChange={(e) => setInquiryForm({...inquiryForm, company: e.target.value})}
                          placeholder="Company name"
                          className="h-10"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm">Email *</Label>
                        <Input
                          type="email"
                          value={inquiryForm.email}
                          onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                          placeholder="your@email.com"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Phone</Label>
                        <Input
                          value={inquiryForm.phone}
                          onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                          placeholder="+91 XXXXX XXXXX"
                          className="h-10"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label className="text-sm">Quantity Required</Label>
                  <Input
                    type="number"
                    value={inquiryForm.quantity}
                    onChange={(e) => setInquiryForm({...inquiryForm, quantity: e.target.value})}
                    placeholder={`e.g., 1000 ${selectedProduct?.unit || 'units'}`}
                    className="h-10"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Your Message *</Label>
                  <Textarea
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                    placeholder="Describe your requirements..."
                    rows={3}
                  />
                </div>
                
                {/* Image Upload Section */}
                <div className="space-y-2">
                  <Label className="text-sm">Reference Images (Optional)</Label>
                  <p className="text-xs text-muted-foreground">
                    Upload up to 3 images to show what you're looking for
                  </p>
                  
                  {inquiryImagePreviews.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {inquiryImagePreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={preview} 
                            alt={`Reference ${index + 1}`}
                            className="w-16 h-16 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removeInquiryImage(index)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {inquiryForm.images.length < 3 && (
                    <div>
                      <input
                        type="file"
                        ref={inquiryFileInputRef}
                        onChange={handleInquiryImageUpload}
                        accept="image/*"
                        multiple
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => inquiryFileInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Add Reference Images
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setShowInquiryDialog(false)} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button onClick={handleSendInquiry} disabled={isSending} className="w-full sm:w-auto">
                  {isSending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Send Inquiry</>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-6 sm:py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">© 2026 GlobalVyapar. Connecting global trade.</p>
        </div>
      </footer>
    </div>
  );
}
